/**
 * <TwoPaneTemplate> — une liste à gauche, le détail de ce qu'on y a ouvert à droite (template).
 *
 * Le gabarit du grand écran : il ne sait rien de ce qu'il porte. Deux fentes et un état.
 *  - Au large (le CONTENEUR mesure `TWO_PANE_FROM` ou plus, `hooks/useWidthAtLeast`) : la liste
 *    dans un volet de `TWO_PANE_LIST_WIDTH`, le détail dans le reste, un filet entre les deux. Rien
 *    d'ouvert : le volet droit montre `empty`.
 *  - Sur un téléphone : un écran à la fois, ceux d'aujourd'hui — la liste, puis, quand quelque
 *    chose est ouvert, le détail seul (avec son retour, que l'appelant pose).
 *
 * Contrôlé : ce qui est ouvert arrive en props (`detail` présent ou `null`), l'appelant l'ouvre et
 * le ferme. Les deux fentes sont des fonctions de la disposition (`split`) : au large, le détail
 * n'a pas de retour et la liste marque ce qui est ouvert ; sur un téléphone, l'inverse.
 *
 * Il ne peint que le filet et le fond du volet vide : chaque volet porte sa page, qui pose son
 * propre fond (ou le laisse à la coquille).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { TWO_PANE_FROM, TWO_PANE_LIST_WIDTH } from "./twoPaneGeometry"

/** `true` : les deux volets côte à côte ; `false` : un écran à la fois. */
export type TwoPaneRender = (split: boolean) => React.ReactNode

export type TwoPaneTemplateProps = {
    /** La liste. */
    list: TwoPaneRender
    /** Le détail de ce qui est ouvert. `null` : rien n'est ouvert. */
    detail: TwoPaneRender | null
    /** Le volet droit quand rien n'est ouvert, au large. Absent : le volet vide. */
    empty?: React.ReactNode
    /**
     * Où se pose le volet de la liste, au large. `left` (défaut) : la liste à gauche, le détail à
     * droite. `right` : le volet étroit à droite, à côté de ce qu'on lit (la copie de l'élève à
     * côté de l'énoncé).
     */
    listSide?: "left" | "right"
    testID?: string
}

export function TwoPaneTemplate(props: TwoPaneTemplateProps) {
    const { list, detail, empty, listSide = "left", testID } = props
    const { onLayout, atLeast: split } = useWidthAtLeast(TWO_PANE_FROM)

    if (!split) {
        return (
            <View onLayout={onLayout} style={styles.root(false)} testID={testID}>
                {detail != null ? detail(false) : list(false)}
            </View>
        )
    }

    const listPane = <View style={listSide === "left" ? styles.list : styles.listRight}>{list(true)}</View>

    return (
        <View onLayout={onLayout} style={styles.root(true)} testID={testID}>
            {listSide === "left" && listPane}
            <View style={styles.detail}>
                {detail != null ? detail(true) : <View style={styles.empty}>{empty}</View>}
            </View>
            {listSide === "right" && listPane}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: (split: boolean) => ({
        flex: 1,
        minHeight: 0,
        flexDirection: split ? ("row" as const) : ("column" as const),
    }),
    list: {
        width: TWO_PANE_LIST_WIDTH,
        flexShrink: 0,
        borderRightWidth: 1,
        borderRightColor: theme.colors.border,
    },
    listRight: {
        width: TWO_PANE_LIST_WIDTH,
        flexShrink: 0,
        borderLeftWidth: 1,
        borderLeftColor: theme.colors.border,
    },
    detail: { flex: 1, minWidth: 0 },
    /** Rien d'ouvert : le fond de page, et ce qui le dit au milieu. */
    empty: {
        flex: 1,
        justifyContent: "center",
        padding: theme.spacing["6"],
        backgroundColor: theme.colors.surface,
    },
}))
